import type { RouterContextType } from "@/api/permissions/permissions.api";
import { Button } from "@/components/ui/Button/Button";
import Header from "@/components/ui/Header/Header";
import { Input } from "@/components/ui/Input/Input";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { Box, Flex, Group, Paper, Stack, Text, Title, UnstyledButton } from "@mantine/core";
import { IconTrash } from "@tabler/icons-react";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import styles from "./addUsers.module.scss";

export const Route = createFileRoute("/entities/create-entity/add-users")({
  component: AddEntityUsers,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function AddEntityUsers() {
  const navigate = useNavigate();
  const [emailFields, setEmailFields] = useState<string[]>([""]);
  const maxEmails = 5;

  const handleAddEmailField = () => {
    if (emailFields.length < maxEmails) {
      setEmailFields([...emailFields, ""]);
    }
  };

  const handleRemoveEmailField = (index: number) => {
    if (emailFields.length > 1) {
      const newFields = emailFields.filter((_, i) => i !== index);
      setEmailFields(newFields);
    }
  };

  const handleEmailChange = (index: number, value: string) => {
    const newFields = [...emailFields];
    newFields[index] = value;
    setEmailFields(newFields);
  };

  const handleInvite = () => {
    const validEmails = emailFields.filter((email) => email.trim() !== "");
    if (validEmails.length > 0) {
      navigate({ to: "/" });
    }
  };

  const handleClose = () => {
    navigate({ to: "/entities/create-entity/success" });
  };

  const canAddMore = emailFields.length < maxEmails;
  const hasValidEmails = emailFields.filter((email) => email.trim() !== "").length > 0;

  return (
    <Box className={styles.container}>
      <Header title="Create New Entity" onClose={handleClose} />

      <Flex direction="column" align="center" className={styles.contentWrapper}>
        <Stack gap="lg" className={styles.contentStack}>
          <Title order={4} className={styles.cardTitle}>
            Add Users
          </Title>
          <Paper radius="md" withBorder shadow="xs" className={styles.card}>
            <Stack gap="md">
              <Text className={styles.instructionText}>
                Enter user emails—each person will be notified and granted access once invited.
              </Text>

              <Stack gap="sm">
                <Text className={styles.fieldLabel}>Add User Emails</Text>
                {emailFields.map((email, index) => (
                  <Box key={index} className={styles.emailFieldWrapper}>
                    <Input
                      type="email"
                      placeholder=""
                      value={email}
                      onChange={(value) => handleEmailChange(index, value)}
                      className={styles.emailInput}
                    />
                    {emailFields.length > 1 && (
                      <UnstyledButton
                        className={styles.removeButton}
                        onClick={() => handleRemoveEmailField(index)}
                        aria-label="Remove email field"
                      >
                        <IconTrash size={16} color="red" />
                      </UnstyledButton>
                    )}
                  </Box>
                ))}

                <UnstyledButton
                  className={styles.addMoreButton}
                  onClick={handleAddEmailField}
                  disabled={!canAddMore}
                  // iconLeft={<IconPlus size={16} />}
                >
                  <Text className={styles.addMoreText}>New User email</Text>
                </UnstyledButton>
              </Stack>
            </Stack>
          </Paper>
          <Group justify="flex-end">
            <Button
              variant="primary"
              size="large"
              onClick={handleInvite}
              isDisabled={!hasValidEmails}
            >
              Invite Users
            </Button>
          </Group>
        </Stack>
      </Flex>
    </Box>
  );
}
